/**
 * Notification bell of the dashboard header.
 *
 * Today its only notifications are the remittance reminders: amounts collected
 * by an aggregator and still unremitted beyond the configured delay. Users who
 * cannot open the remittance screens keep the plain bell with no badge.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Tooltip,
  Typography,
  alpha,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  CheckCircleOutline as AllGoodIcon,
  Notifications as NotificationsIcon,
} from '@mui/icons-material';

import { useRemittanceAccess, useRemittanceReminders } from '../../hooks/useRemittances';
import { useTheme } from '../../store/ThemeContext';
import { AGGREGATOR_LOGOS, formatMoney } from '../../utils/remittances';

export default function RemittanceRemindersBell() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const muiTheme = useMuiTheme();
  const { colors } = useTheme();
  const { canAccess } = useRemittanceAccess();
  const { data } = useRemittanceReminders();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const count = canAccess ? data?.count ?? 0 : 0;
  const hasCritical = (data?.critical_count ?? 0) > 0;
  const currency = data?.currency_code || 'XAF';

  const go = (path: string) => {
    setAnchor(null);
    navigate(path);
  };

  return (
    <>
      <Tooltip title={t('dashboard.notifications')}>
        <IconButton
          sx={{ color: muiTheme.palette.text.primary }}
          onClick={(event) => canAccess && setAnchor(event.currentTarget)}
          aria-label={t('dashboard.notifications')}
        >
          <Badge
            badgeContent={count}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': hasCritical
                ? {}
                : {
                    backgroundColor: colors.secondary.main,
                    color: colors.secondary.contrastText,
                  },
            }}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxWidth: 'calc(100vw - 32px)', mt: 1 } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t('remittances.reminders.bellTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data
              ? t('remittances.reminders.rules', { late: data.late_days, critical: data.critical_days })
              : t('common.loading')}
          </Typography>
        </Box>
        <Divider />
        {count === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <AllGoodIcon sx={{ fontSize: 40, color: muiTheme.palette.success.main, mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('remittances.reminders.empty')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 360, overflow: 'auto' }}>
            {data?.items.map((item) => {
              const critical = item.status === 'CRITICAL';
              const tone = critical ? muiTheme.palette.error.main : muiTheme.palette.warning.main;
              return (
                <ListItemButton
                  key={`${item.agency_id}-${item.aggregator}`}
                  onClick={() => go('/dashboard/finance/remittances/tracking')}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1.5,
                    py: 1.25,
                    borderLeft: `3px solid ${tone}`,
                    '&:hover': { backgroundColor: alpha(tone, 0.06) },
                  }}
                >
                  <Box
                    component="img"
                    src={AGGREGATOR_LOGOS[item.aggregator]}
                    alt=""
                    sx={{ width: 40, height: 26, objectFit: 'contain', mt: 0.25 }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={700} noWrap sx={{ flexGrow: 1 }}>
                        {t(`remittances.aggregators.${item.aggregator}`)} · {item.agency_name}
                      </Typography>
                      <Chip
                        size="small"
                        color={critical ? 'error' : 'warning'}
                        label={t(`remittances.trackingStatuses.${item.status}`)}
                      />
                    </Box>
                    <Typography variant="body2">
                      {t('remittances.reminders.item', {
                        amount: formatMoney(item.balance, currency),
                        days: item.age_days,
                      })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('remittances.reminders.overdue', {
                        amount: formatMoney(item.overdue_amount, currency),
                      })}
                    </Typography>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
        <Divider />
        <Box sx={{ display: 'flex', gap: 1, p: 1.5, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={() => go('/dashboard/finance/remittances/tracking')}>
            {t('remittances.reminders.viewTracking')}
          </Button>
          <Button size="small" variant="contained" onClick={() => go('/dashboard/finance/remittances/new')}>
            {t('remittances.recordTitle')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}
